/* Global overlays opened from anywhere: help drawer, AI assistant, command palette,
 * notifications, role sheet, tour. Also helpers to open the Print Center. */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

export type Overlay =
  | { kind: "help"; topic?: string }
  | { kind: "assistant" }
  | { kind: "cmd" }
  | { kind: "notifs" }
  | { kind: "role" }
  | { kind: "tour" };

export interface PrintOptions { doc?: string; emp?: number; period?: string; scope?: string; letter?: string; date?: string }

interface Ctx {
  overlay: Overlay | null;
  open(o: Overlay): void;
  close(): void;
  openPrint(o?: PrintOptions): void;
  navOpen: boolean;
  setNavOpen(v: boolean): void;
  collapsed: boolean;
  setCollapsed(v: boolean): void;
}
const C = createContext<Ctx | null>(null);

export function OverlayProvider({ children }: { children: ReactNode }) {
  const [overlay, setOverlay] = useState<Overlay | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const nav = useNavigate();
  const open = useCallback((o: Overlay) => setOverlay(o), []);
  const close = useCallback(() => setOverlay(null), []);
  const openPrint = useCallback((o: PrintOptions = {}) => {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(o)) if (v != null && v !== "") q.set(k, String(v));
    const s = q.toString();
    nav("/print" + (s ? "?" + s : ""));
  }, [nav]);
  const v = useMemo(() => ({ overlay, open, close, openPrint, navOpen, setNavOpen, collapsed, setCollapsed }), [overlay, open, close, openPrint, navOpen, collapsed]);
  return <C.Provider value={v}>{children}</C.Provider>;
}

export function useOverlays(): Ctx {
  const c = useContext(C);
  if (!c) throw new Error("useOverlays() outside <OverlayProvider>");
  return c;
}
